import { EventBus, Events } from '../../managers/EventBus';
import { Tooltip } from './Tooltip';
import { buildStatusEffectTooltip } from './TooltipContent';
import { buildElementTooltip } from './ElementTooltip';
import { renderUnitPortrait } from './UnitPortrait';
import { getStatusIconUrl, getElementIconUrl } from './ItemIcon';
import type { CombatantPosition, CombatantPositionsPayload, ServerStatusEffect, ServerBarrierInstance } from '../../types';

interface HPBarState {
  id: string;
  current: number;
  max: number;
  shield: number;
  /** Active barrier instances. Used to render segmented barrier bar. */
  barriers: ServerBarrierInstance[];
  name: string;
  isEnemy: boolean;
  /** Defensive types (1-2 entries). Rendered as small icons before the name on enemy bars only. */
  elements?: string[];
  statusEffects: ServerStatusEffect[];
  tempo?: number;
  focus?: number;
  maxFocus?: number;
  // ── Break system (per-enemy fill bar) ──
  /** Accumulated break fill on this combatant. Bar visible once first contribution lands. */
  breakFill?: number;
  /** Threshold to trigger Broken state. 0 = no break bar (trash mobs). */
  breakThreshold?: number;
  /** Sticky for the encounter — once any contribution lands, stays visible. */
  breakBarRevealed?: boolean;
  /** Latched true the instant break_trigger fires so the bar renders at 100% in the
   *  broken palette without waiting for STATUS_EFFECTS_UPDATE. Cleared by the next
   *  status-effects update (authoritative). Without this, the bar visibly drained
   *  from 100% → 0% in the window between the two events. */
  brokenLocked?: boolean;
}

/** Per-source barrier color palette */
const BARRIER_COLORS: Record<string, string> = {
  brinewall:            '#66ccff',
  tideguard:            '#1166bb',
  iron_wall:            '#cc9944',
  battleward:           '#88aacc',
  turn_start_barrier:   '#22aa88',
};
const BARRIER_DEFAULT_COLOR = '#66ccff';

function barrierColor(sourceId: string): string {
  return BARRIER_COLORS[sourceId] ?? BARRIER_DEFAULT_COLOR;
}

function sumBarriers(barriers: ServerBarrierInstance[] | undefined): number {
  if (!barriers || barriers.length === 0) return 0;
  let total = 0;
  for (const b of barriers) total += b.amount;
  return total;
}

/**
 * Manages floating HP bars that are positioned above sprites.
 * Listens to COMBATANT_POSITIONS_UPDATED from CombatScene to reposition.
 */
export interface TargetPickerEntry {
  id: string;
  name: string;
  spriteKey: string;
}

export class CombatHUD {
  private container: HTMLElement | null = null;
  private pickerContainer: HTMLElement | null = null;
  private bars = new Map<string, HTMLElement>();
  private state = new Map<string, HPBarState>();
  private unsubs: Array<() => void> = [];
  /** IDs that should fade in when their bar is first created (room transitions). */
  private pendingFadeIn = new Set<string>();
  /** Last sprite position per bar id — kept so the ResizeObserver below can
   *  re-clamp the bar when its rendered height changes after initial spawn
   *  (async icon image loads, Break bar appearing, status icons added). */
  private positionCache = new Map<string, CombatantPosition>();
  private resizeObservers = new Map<string, ResizeObserver>();

  initialize(): void {
    this.container = document.getElementById('floating-hud');
    this.pickerContainer = document.getElementById('target-picker');
    if (!this.container) return;

    this.unsubs.push(
      EventBus.on(
        Events.COMBATANT_POSITIONS_UPDATED,
        (payload: unknown) => this.onPositionsUpdated(payload as CombatantPositionsPayload),
      ),
      // Listen for HP_UPDATE from EventPlaybackManager
      EventBus.on(
        Events.HP_UPDATE,
        (payload: unknown) => {
          const { id, hp, shield, barriers, focus, tempo } = payload as { id: string; hp?: number; shield?: number; barriers?: ServerBarrierInstance[]; focus?: number; tempo?: number };
          this.updateHP(id, hp, shield, focus, barriers, tempo);
        },
      ),
      // Fade out and remove bar when a combatant is defeated
      EventBus.on(
        Events.COMBAT_ENEMY_DEFEATED,
        (targetId: unknown) => {
          if (typeof targetId !== 'string') return;
          const el = this.bars.get(targetId);
          if (el) {
            el.style.transition = 'opacity 0.4s ease';
            el.style.opacity = '0';
            setTimeout(() => this.removeBar(targetId), 400);
          }
        },
      ),
      // Register summoned enemies for HP tracking
      EventBus.on(
        Events.COMBAT_ENEMY_SUMMONED,
        (payload: unknown) => {
          const data = payload as { id: string; name: string; hp: number; maxHp: number; defensiveTypes?: string[] };
          this.state.set(data.id, {
            id: data.id,
            current: data.hp,
            max: data.maxHp,
            shield: 0,
            barriers: [],
            name: data.name,
            isEnemy: true,
            elements: data.defensiveTypes && data.defensiveTypes.length > 0 ? data.defensiveTypes : undefined,
            statusEffects: [],
          });
        },
      ),
      // Register player-summoned minions for HP tracking (player-side, isEnemy: false)
      EventBus.on(
        Events.COMBAT_ALLY_SUMMONED,
        (payload: unknown) => {
          const data = payload as { id: string; name: string; hp: number; maxHp: number; defensiveTypes?: string[] };
          this.state.set(data.id, {
            id: data.id,
            current: data.hp,
            max: data.maxHp,
            shield: 0,
            barriers: [],
            name: data.name,
            isEnemy: false,
            elements: data.defensiveTypes && data.defensiveTypes.length > 0 ? data.defensiveTypes : undefined,
            statusEffects: [],
          });
        },
      ),
      // Update status effect icons on HP bars
      EventBus.on(
        Events.STATUS_EFFECTS_UPDATE,
        (payload: unknown) => {
          const { id, statusEffects } = payload as { id: string; statusEffects: ServerStatusEffect[] };
          const s = this.state.get(id);
          if (s) {
            s.statusEffects = statusEffects;
            // Authoritative status now in place — the temporary brokenLocked latch from
            // the break_trigger flourish can release.
            s.brokenLocked = false;
            this.renderBar(id);
          }
        },
      ),
      // Per-enemy Break bar fill / threshold-trigger updates.
      EventBus.on(
        Events.BREAK_UPDATE,
        (payload: unknown) => {
          const { id, fill, threshold, broken } = payload as { id: string; fill: number; threshold: number; broken?: boolean };
          const s = this.state.get(id);
          if (!s) return;
          s.breakFill = fill;
          s.breakThreshold = threshold;
          s.breakBarRevealed = true;
          if (broken) {
            // Latch the broken UI state immediately. renderBar() OR the status-effects
            // update would otherwise see fill=0 and an empty statusEffects array, and
            // animate the bar visibly from 100% → 0% before the broken status arrives.
            s.brokenLocked = true;
          }
          this.renderBar(id);
          if (broken) {
            const el = this.bars.get(id);
            if (el) {
              el.classList.add('break-triggered');
              this.spawnBreakShards(el);
              // 900ms covers the longest sub-animation (shard fly-out) plus settle.
              setTimeout(() => el.classList.remove('break-triggered'), 900);
            }
          }
        },
      ),
    );
  }

  destroy(): void {
    for (const unsub of this.unsubs) unsub();
    this.unsubs = [];
    for (const ro of this.resizeObservers.values()) ro.disconnect();
    this.resizeObservers.clear();
    this.positionCache.clear();
    this.bars.clear();
    this.state.clear();
    if (this.container) this.container.innerHTML = '';
  }

  /** Set initial HP state for all combatants. Call after combat data is known. */
  setCombatants(combatants: HPBarState[]): void {
    this.state.clear();
    for (const c of combatants) {
      this.state.set(c.id, { ...c, barriers: c.barriers ?? [] });
    }
    // Re-render bars that were created before state was available
    for (const id of this.bars.keys()) {
      this.renderBar(id);
    }
  }

  /** Room transition: remove enemy bars and add new enemies, keep player bars intact. */
  transitionEnemies(newCombatants: HPBarState[]): void {
    // Remove enemy bars + state
    for (const [id, s] of this.state) {
      if (s.isEnemy) {
        const el = this.bars.get(id);
        if (el) el.remove();
        this.resizeObservers.get(id)?.disconnect();
        this.resizeObservers.delete(id);
        this.positionCache.delete(id);
        this.bars.delete(id);
        this.state.delete(id);
      }
    }
    // Update player state (HP may have changed from carry-over)
    for (const c of newCombatants) {
      if (!c.isEnemy) {
        this.state.set(c.id, { ...c, barriers: c.barriers ?? [] });
        this.renderBar(c.id);
      }
    }
    // Add new enemies (marked for fade-in when bar is created)
    for (const c of newCombatants) {
      if (c.isEnemy) {
        this.state.set(c.id, { ...c, barriers: c.barriers ?? [] });
        this.pendingFadeIn.add(c.id);
      }
    }
  }

  /** Highlight the targeted combatant's HP bar. Pass null to clear. */
  setTarget(targetId: string | null): void {
    for (const [id, el] of this.bars) {
      el.classList.toggle('targeted', id === targetId);
    }
  }

  /** Toggle targeting mode — makes bars clickable to indicate they're selectable. */
  setTargetingMode(active: boolean, mode?: 'single' | 'all', side?: 'enemy' | 'player'): void {
    // On narrow viewports the target picker strip replaces HP bar clicking
    const pickerActive = this.pickerContainer?.classList.contains('visible') ?? false;
    const targetSide = side ?? 'enemy';
    for (const [id, el] of this.bars) {
      const s = this.state.get(id);
      const isTargetSide = targetSide === 'enemy' ? s?.isEnemy : !s?.isEnemy;
      if (isTargetSide) {
        el.classList.toggle('targetable', active && !pickerActive && mode !== 'all');
        el.classList.toggle('targetable-all', active && !pickerActive && mode === 'all');
        el.classList.toggle('targetable-ally', active && !pickerActive && targetSide === 'player');
      } else {
        // Clear targeting from the other side
        el.classList.remove('targetable', 'targetable-all', 'targetable-ally');
      }
    }
  }

  /** Update a single combatant's HP and optionally shield/focus/barriers/tempo. */
  updateHP(id: string, current?: number, shield?: number, focus?: number, barriers?: ServerBarrierInstance[], tempo?: number): void {
    const s = this.state.get(id);
    if (!s) return;

    if (current != null) s.current = Math.max(0, current);
    if (barriers != null) {
      s.barriers = barriers;
      s.shield = sumBarriers(barriers);
    } else if (shield != null) {
      s.shield = shield;
    }
    if (focus != null) s.focus = focus;
    if (tempo != null) s.tempo = tempo;
    this.renderBar(id);
  }

  /** Remove a combatant's HP bar from the DOM. */
  private removeBar(id: string): void {
    const el = this.bars.get(id);
    if (el) {
      el.remove();
      this.bars.delete(id);
    }
    this.resizeObservers.get(id)?.disconnect();
    this.resizeObservers.delete(id);
    this.positionCache.delete(id);
  }

  /** Crystalline-shard burst on the Break bar the moment it trips. Spawns 7
   *  triangular DOM shards from the bar's right edge (where the fill just
   *  completed), each fired at a randomised angle in the upper hemisphere. */
  private spawnBreakShards(wrapper: HTMLElement): void {
    const row = wrapper.querySelector('.break-bar-row') as HTMLElement | null;
    if (!row) return;
    const count = 7;
    for (let i = 0; i < count; i++) {
      const shard = document.createElement('span');
      shard.className = 'break-shard';
      row.appendChild(shard);
      // Cone biased upward and to the right — that's where the fill just reached.
      const angleDeg = -110 + (i / (count - 1)) * 120 + (Math.random() - 0.5) * 18;
      const angle = (angleDeg * Math.PI) / 180;
      const dist = 22 + Math.random() * 16;
      const dx = Math.cos(angle) * dist;
      const dy = Math.sin(angle) * dist;
      const rot = (Math.random() - 0.5) * 120;
      const duration = 460 + Math.random() * 140;
      shard.animate(
        [
          { transform: 'translate(0, -50%) rotate(0deg) scale(1)', opacity: 1 },
          { transform: `translate(${dx}px, calc(-50% + ${dy}px)) rotate(${rot}deg) scale(1.3)`, opacity: 0 },
        ],
        { duration, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)', delay: i * 14, fill: 'forwards' },
      );
      setTimeout(() => shard.remove(), duration + 80);
    }
  }

  // ---------------------------------------------------------------------------

  private onPositionsUpdated(payload: CombatantPositionsPayload): void {
    if (!this.container) return;

    for (const pos of payload.positions) {
      let el = this.bars.get(pos.id);
      if (!el) {
        el = this.createBarElement(pos);
        // Set initial HP width BEFORE appending so the CSS transition doesn't
        // animate from the default 100% down to the actual value.
        const s = this.state.get(pos.id);
        const fill = el.querySelector('.hp-fill') as HTMLElement | null;
        if (fill && s) {
          const pct = s.max > 0 ? (s.current / s.max) * 100 : 0;
          fill.style.transition = 'none';
          fill.style.width = `${pct}%`;
        }
        // Set initial focus fill similarly
        const focusFill = el.querySelector('.focus-fill') as HTMLElement | null;
        if (focusFill && s && s.maxFocus && s.maxFocus > 0) {
          const focusPct = (s.focus ?? 0) / s.maxFocus * 100;
          focusFill.style.transition = 'none';
          focusFill.style.width = `${focusPct}%`;
        }
        this.container.appendChild(el);
        this.bars.set(pos.id, el);
        // Watch for height changes after spawn — async icon loads, Break bar
        // becoming visible, status icons appearing — and re-clamp each time so
        // the bar's top edge never bleeds into the timeline.
        const barId = pos.id;
        const ro = new ResizeObserver(() => {
          const cached = this.positionCache.get(barId);
          const barEl = this.bars.get(barId);
          if (cached && barEl) this.clampBarPosition(barEl, cached);
        });
        ro.observe(el);
        this.resizeObservers.set(barId, ro);
        // Fade in bars for room-transition enemies
        if (this.pendingFadeIn.has(pos.id)) {
          this.pendingFadeIn.delete(pos.id);
          el.classList.add('hp-fade-in');
        }
        // Re-enable transition after the browser paints the initial width
        if (fill) requestAnimationFrame(() => { fill.style.transition = ''; });
        if (focusFill) requestAnimationFrame(() => { focusFill.style.transition = ''; });
      }
      this.positionCache.set(pos.id, pos);
      this.clampBarPosition(el, pos);
    }

    // Render all bars with current state
    for (const id of this.bars.keys()) {
      this.renderBar(id);
    }
  }

  /** Position a single bar so its TOP edge never overlaps the timeline strip.
   *  Two anchoring modes, picked by whether the bar fits above the sprite:
   *   - Normal:   anchor BOTTOM above sprite head, content grows upward
   *               (transform: translate(-50%, -100%) — `top` = bar's bottom Y)
   *   - Clamped:  anchor TOP just below timeline, content grows downward
   *               (transform: translate(-50%, 0)    — `top` = bar's top Y)
   *  Re-runs on every state change and on every bar-height change (ResizeObserver),
   *  so async icon loads or Break-bar appearances don't bleed the bar into the timeline. */
  private clampBarPosition(el: HTMLElement, pos: CombatantPosition): void {
    const topBar = document.getElementById('combat-top-bar');
    // 12px = "section gap" range from the design system, between two distinct UI surfaces.
    const timelineBottom = (topBar ? topBar.offsetHeight : 90) + 12;
    const barHeight = el.offsetHeight || 60;
    // pos.y is sprite feet (origin 0.5, 1.0); 22px buffer above the head.
    const naturalBottom = pos.y - pos.displayHeight - 22;

    if (naturalBottom - barHeight >= timelineBottom) {
      el.style.top = `${naturalBottom}px`;
      el.style.transform = 'translate(-50%, -100%)';
    } else {
      el.style.top = `${timelineBottom}px`;
      el.style.transform = 'translate(-50%, 0)';
    }
    el.style.left = `${pos.x}px`;
  }

  private createBarElement(pos: CombatantPosition): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = `floating-hp ${pos.isEnemy ? 'enemy' : 'player'}`;
    wrapper.dataset.combatantId = pos.id;

    wrapper.innerHTML = `
      <div class="hp-name-row">
        <div class="tempo-pips" style="display:none"></div>
        <span class="hp-name"></span>
      </div>
      <div class="hp-bar-row">
        <span class="hp-elements" style="display:none" aria-hidden="true"></span>
        <div class="hp-bar"><div class="hp-fill"></div><div class="barrier-segments"></div><span class="hp-text"></span></div>
        <div class="status-effects"></div>
      </div>
      <div class="break-bar-row" style="display:none">
        <div class="break-bar"><div class="break-fill"></div></div>
      </div>
      <div class="hp-sub-row">
        <span class="hp-barrier-badge" style="display:none"><span class="hp-barrier-icon"></span><span class="hp-barrier-value"></span></span>
      </div>
    `;

    // Click to select as target (only fires when in targeting mode)
    wrapper.addEventListener('click', () => {
      if (wrapper.classList.contains('targetable') || wrapper.classList.contains('targetable-all')) {
        EventBus.emit(Events.COMBAT_TARGET_SELECTED, pos.id);
      }
    });

    // Tooltip on the bar and barrier badge showing barrier breakdown
    const barrierTooltipFn = () => {
      const s = this.state.get(pos.id);
      if (!s || !s.barriers || s.barriers.length === 0) return '';
      return buildBarrierTooltip(s.barriers);
    };
    const bar = wrapper.querySelector('.hp-bar') as HTMLElement | null;
    if (bar) Tooltip.attach(bar, barrierTooltipFn);
    const badge = wrapper.querySelector('.hp-barrier-badge') as HTMLElement | null;
    if (badge) Tooltip.attach(badge, barrierTooltipFn);

    // Break bar tooltip — explains the mechanic. Element is created hidden;
    // when shown (after first contribution) hover/long-press fires this.
    const breakBar = wrapper.querySelector('.break-bar') as HTMLElement | null;
    if (breakBar) Tooltip.attach(breakBar, () => buildBreakTooltip());

    return wrapper;
  }

  private renderBar(id: string): void {
    const el = this.bars.get(id);
    const s = this.state.get(id);
    if (!el || !s) return;

    const nameEl = el.querySelector('.hp-name') as HTMLElement;
    const fillEl = el.querySelector('.hp-fill') as HTMLElement;
    const segmentsEl = el.querySelector('.barrier-segments') as HTMLElement;
    const textEl = el.querySelector('.hp-text') as HTMLElement;

    if (nameEl) nameEl.textContent = s.name;

    // Element-type icons. Only rendered for enemies — player typing is shown
    // elsewhere (party/codex). In combat the question is "what am I fighting?".
    // Each icon hovers/long-presses to a tooltip listing weaknesses and resists.
    const elementsEl = el.querySelector('.hp-elements') as HTMLElement | null;
    if (elementsEl) {
      if (s.isEnemy && s.elements && s.elements.length > 0) {
        const html = s.elements
          .map(e => `<img class="hp-element-icon" src="${getElementIconUrl(e)}" alt="" data-element="${e}">`)
          .join('');
        if (elementsEl.innerHTML !== html) {
          elementsEl.innerHTML = html;
          elementsEl.querySelectorAll('.hp-element-icon').forEach(iconEl => {
            const element = (iconEl as HTMLElement).dataset.element;
            if (element) Tooltip.attach(iconEl as HTMLElement, () => buildElementTooltip(element));
          });
        }
        elementsEl.style.display = '';
      } else if (elementsEl.style.display !== 'none') {
        elementsEl.innerHTML = '';
        elementsEl.style.display = 'none';
      }
    }
    if (fillEl) {
      const pct = s.max > 0 ? (s.current / s.max) * 100 : 0;
      fillEl.style.width = `${pct}%`;
    }
    // Segmented barrier overlay: one segment per active barrier instance
    if (segmentsEl) {
      this.renderBarrierSegments(segmentsEl, s);
    }
    if (textEl) {
      textEl.textContent = `${s.current}/${s.max}`;
    }
    const badgeEl = el.querySelector('.hp-barrier-badge') as HTMLElement;
    const badgeVal = el.querySelector('.hp-barrier-value') as HTMLElement;
    if (badgeEl && badgeVal) {
      if (s.shield > 0) {
        badgeVal.textContent = `${s.shield}`;
        badgeEl.style.display = '';
      } else {
        badgeEl.style.display = 'none';
      }
    }

    // Break bar — per-enemy fill toward Broken state. Only visible once first contribution
    // lands; sticky for the encounter. Hidden entirely on player bars and on enemies with
    // breakThreshold = 0 (trash mobs).
    const breakRowEl = el.querySelector('.break-bar-row') as HTMLElement | null;
    const breakFillEl = el.querySelector('.break-fill') as HTMLElement | null;
    if (breakRowEl && breakFillEl) {
      // brokenLocked covers the gap between break_trigger and the subsequent
      // status-effects update so the bar stays at 100% in the broken palette
      // through the flourish window. Cleared by STATUS_EFFECTS_UPDATE.
      const isBroken = !!s.brokenLocked || s.statusEffects.some(e => e.type === 'broken');
      const showBar = s.isEnemy && (s.breakBarRevealed || isBroken) && (s.breakThreshold ?? 0) > 0;
      if (showBar) {
        const wasHidden = breakRowEl.style.display === 'none' || breakRowEl.style.display === '' && !breakRowEl.dataset.shown;
        const pct = isBroken
          ? 100
          : s.breakThreshold && s.breakThreshold > 0
            ? Math.max(0, Math.min(100, ((s.breakFill ?? 0) / s.breakThreshold) * 100))
            : 0;
        breakFillEl.style.width = `${pct}%`;
        breakRowEl.style.display = '';
        breakRowEl.classList.toggle('is-broken', isBroken);
        // Approaching-break warning pulse at ≥80% fill (pre-Broken only).
        breakRowEl.classList.toggle('is-almost', !isBroken && pct >= 80);
        // Wrapper flag — HP bar squares its bottom corners when the break bar
        // is sitting flush below it, so the two read as one continuous meter.
        el.classList.add('has-break-bar');
        // First reveal: wipe-in animation. Tracked via data attr so multiple
        // renderBar passes within the same frame don't re-trigger it. Set BEFORE
        // adding the class so the class-toggle fires exactly once.
        if (wasHidden) {
          breakRowEl.dataset.shown = '1';
          // Re-trigger CSS animation by removing + reattaching the class on the
          // next frame (otherwise an already-attached class wouldn't restart).
          breakRowEl.classList.remove('break-bar-entering');
          requestAnimationFrame(() => {
            breakRowEl.classList.add('break-bar-entering');
            setTimeout(() => breakRowEl.classList.remove('break-bar-entering'), 460);
          });
        }
      } else {
        breakRowEl.style.display = 'none';
        breakRowEl.classList.remove('break-bar-entering');
        delete breakRowEl.dataset.shown;
        el.classList.remove('has-break-bar');
      }
    }

    // Tempo pips — combo-point style, one pip per stack. Inline with name because
    // it's a primary decision input for Subterfuge builds. Uncapped: we render as
    // many pips as the target has (typical range 1-6). Each new pip pops in via
    // CSS animation so building stacks feels satisfying.
    const tempoPipsEl = el.querySelector('.tempo-pips') as HTMLElement;
    if (tempoPipsEl) {
      const count = s.tempo ?? 0;
      const prevCount = tempoPipsEl.childElementCount;
      if (count > 0) {
        // Only rebuild if count changed — avoids re-triggering pop-in on every render
        if (count !== prevCount) {
          tempoPipsEl.innerHTML = '';
          for (let i = 0; i < count; i++) {
            const pip = document.createElement('span');
            pip.className = 'tempo-pip';
            // Only the most recently added pips get the pop-in animation
            if (i >= prevCount) pip.classList.add('tempo-pip--new');
            tempoPipsEl.appendChild(pip);
          }
        }
        tempoPipsEl.style.display = '';
        const tempoIconUrl = getStatusIconUrl('tempo');
        Tooltip.attach(tempoPipsEl, () =>
          `<div class="tooltip-inner">` +
            `<div class="tooltip-header">` +
              `<span class="tooltip-name-group">` +
                `<img class="tooltip-icon" src="${tempoIconUrl}" width="28" height="28" alt="" draggable="false">` +
                `<span class="tooltip-name">Tempo</span>` +
              `</span>` +
              `<span class="tooltip-element">${count} stack${count === 1 ? '' : 's'}</span>` +
            `</div>` +
            `<div class="tooltip-line"><span class="label">Built by fast attacks. Consumed by spender abilities for bonus damage, crits, stuns, or lifesteal.</span></div>` +
            `<div class="tooltip-separator"></div>` +
            `<div class="tooltip-line"><span class="label">Resets when this unit starts its turn.</span></div>` +
          `</div>`
        );
      } else {
        tempoPipsEl.style.display = 'none';
        tempoPipsEl.innerHTML = '';
      }
    }

    // Render status effect icons beside HP bar
    const statusEl = el.querySelector('.status-effects') as HTMLElement;
    if (statusEl) {
      statusEl.innerHTML = '';
      for (const effect of s.statusEffects) {
        const icon = document.createElement('img');
        icon.className = 'status-icon';
        icon.src = getStatusIconUrl(effect.id);
        icon.width = 18;
        icon.height = 18;
        icon.draggable = false;
        // Stackable effects: wrap icon so we can render a stack badge in the corner
        if (effect.stackCount && effect.stackCount > 1) {
          const wrap = document.createElement('span');
          wrap.className = 'status-icon-wrap';
          wrap.appendChild(icon);
          const badge = document.createElement('span');
          badge.className = 'status-stack-badge';
          badge.textContent = String(effect.stackCount);
          wrap.appendChild(badge);
          Tooltip.attach(wrap, () => buildStatusEffectTooltip(effect));
          statusEl.appendChild(wrap);
        } else {
          Tooltip.attach(icon, () => buildStatusEffectTooltip(effect));
          statusEl.appendChild(icon);
        }
      }
    }

    // Focus bar (player-side only, when maxFocus > 0)
    const focusRow = el.querySelector('.focus-bar-row') as HTMLElement;
    if (focusRow) {
      if (!s.isEnemy && s.maxFocus && s.maxFocus > 0) {
        focusRow.style.display = '';
        const focusFillEl = focusRow.querySelector('.focus-fill') as HTMLElement;
        if (focusFillEl) {
          const prevPct = parseFloat(focusFillEl.style.width) || 0;
          const focusPct = s.maxFocus > 0 ? ((s.focus ?? 0) / s.maxFocus) * 100 : 0;
          focusFillEl.style.width = `${focusPct}%`;

          // Animate on change: pulse bright on spend/gain
          if (Math.abs(focusPct - prevPct) > 0.5) {
            const cls = focusPct < prevPct ? 'focus-spent' : 'focus-gained';
            focusFillEl.classList.remove('focus-spent', 'focus-gained');
            void focusFillEl.offsetWidth; // force reflow to restart animation
            focusFillEl.classList.add(cls);
          }

        }
        const focusTextEl = focusRow.querySelector('.focus-text') as HTMLElement;
        if (focusTextEl) {
          focusTextEl.textContent = `${Math.floor(s.focus ?? 0)}/${s.maxFocus}`;
        }
      } else {
        focusRow.style.display = 'none';
      }
    }
  }

  /**
   * Render the segmented barrier overlay. Each barrier instance becomes its own
   * <div.barrier-segment> sized as its share of maxHp on the FULL bar width and
   * positioned to start at the right edge of the HP fill. The container spans
   * the whole bar so segment widths share the same scale as the HP fill;
   * overflow past 100% is clipped by .hp-bar's overflow:hidden.
   * Up to MAX_VISIBLE segments rendered; extras aggregated into a "+N more" segment.
   */
  private renderBarrierSegments(container: HTMLElement, s: HPBarState): void {
    const MAX_VISIBLE = 5;
    if (!s.barriers || s.barriers.length === 0 || s.max <= 0) {
      container.innerHTML = '';
      container.style.display = 'none';
      return;
    }
    container.style.display = '';

    const hpPct = (s.current / s.max) * 100;

    const sorted = [...s.barriers].sort((a, b) => b.amount - a.amount);

    let visible = sorted;
    let aggregated: { count: number; amount: number } | null = null;
    if (sorted.length > MAX_VISIBLE) {
      visible = sorted.slice(0, MAX_VISIBLE - 1);
      const rest = sorted.slice(MAX_VISIBLE - 1);
      aggregated = {
        count: rest.length,
        amount: rest.reduce((sum, b) => sum + b.amount, 0),
      };
    }

    // Reuse segments by sourceId so position transitions animate the damage
    // instead of wiping the DOM and re-running the pop-in keyframe.
    const existing = new Map<string, HTMLElement>();
    container.querySelectorAll<HTMLElement>('.barrier-segment').forEach(el => {
      const key = el.dataset.sourceId;
      if (key) existing.set(key, el);
    });

    type Desired = { key: string; leftPct: number; widthPct: number; background: string; isAggregate: boolean; title?: string };
    const desired: Desired[] = [];
    let cursor = hpPct;
    for (const b of visible) {
      const widthPct = (b.amount / s.max) * 100;
      desired.push({
        key: b.sourceId,
        leftPct: cursor,
        widthPct,
        background: barrierColor(b.sourceId),
        isAggregate: false,
      });
      cursor += widthPct;
    }
    if (aggregated) {
      const widthPct = (aggregated.amount / s.max) * 100;
      desired.push({
        key: '__aggregate__',
        leftPct: cursor,
        widthPct,
        background: BARRIER_DEFAULT_COLOR,
        isAggregate: true,
        title: `+${aggregated.count} more`,
      });
    }

    const desiredKeys = new Set(desired.map(d => d.key));
    existing.forEach((el, key) => {
      if (!desiredKeys.has(key)) el.remove();
    });

    desired.forEach((d, idx) => {
      let seg = existing.get(d.key);
      if (!seg) {
        seg = document.createElement('div');
        seg.dataset.sourceId = d.key;
        seg.className = d.isAggregate
          ? 'barrier-segment barrier-segment--aggregate barrier-segment--new'
          : 'barrier-segment barrier-segment--new';
        seg.style.background = d.background;
        seg.style.left = `${d.leftPct}%`;
        seg.style.width = `${d.widthPct}%`;
        // Drop the --new class once the pop-in finishes so future reorders
        // don't replay the entry animation.
        const segRef = seg;
        window.setTimeout(() => segRef.classList.remove('barrier-segment--new'), 220);
      } else {
        seg.style.left = `${d.leftPct}%`;
        seg.style.width = `${d.widthPct}%`;
        if (seg.style.background !== d.background) seg.style.background = d.background;
        seg.classList.toggle('barrier-segment--aggregate', d.isAggregate);
      }
      if (d.title) seg.title = d.title; else seg.removeAttribute('title');

      // Place segment at the correct index (insertBefore is a no-op if the
      // element is already at that position, which avoids unnecessary moves).
      const refNode = container.children[idx] ?? null;
      if (refNode !== seg) container.insertBefore(seg, refNode);
    });
  }

  // ---------------------------------------------------------------------------
  // Target Picker Strip (mobile)
  // ---------------------------------------------------------------------------

  /** Show the target picker strip with tappable unit portraits (portrait/mobile only). */
  showTargetPicker(targets: TargetPickerEntry[], mode: 'single' | 'all', side: 'enemy' | 'player' = 'enemy'): void {
    if (!this.pickerContainer) return;

    const isNarrow = window.innerWidth / window.innerHeight < 1;
    if (!isNarrow) return;

    this.pickerContainer.innerHTML = '';
    this.pickerContainer.classList.add('visible');
    this.pickerContainer.classList.toggle('mode-all', mode === 'all');
    this.pickerContainer.classList.toggle('mode-ally', side === 'player');

    for (const target of targets) {
      const btn = document.createElement('button');
      btn.className = 'target-picker-btn';
      btn.innerHTML = `
        <div class="timeline-icon">${renderUnitPortrait({ spriteKey: target.spriteKey, name: target.name, size: 'sm' })}</div>
        <span class="target-picker-name">${target.name}</span>
      `;
      btn.addEventListener('pointerenter', () => {
        EventBus.emit('COMBAT_TARGET_HOVERED', target.id);
      });
      btn.addEventListener('pointerleave', () => {
        EventBus.emit('COMBAT_TARGET_HOVERED', null);
      });
      btn.addEventListener('click', () => {
        EventBus.emit(Events.COMBAT_TARGET_SELECTED, target.id);
      });
      this.pickerContainer.appendChild(btn);
    }

    // Cancel button
    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'target-picker-cancel';
    cancelBtn.textContent = '\u2715';
    cancelBtn.addEventListener('click', () => {
      EventBus.emit('COMBAT_CANCEL_TARGETING');
    });
    this.pickerContainer.appendChild(cancelBtn);
  }

  /** Hide the mobile target picker strip. */
  hideTargetPicker(): void {
    if (!this.pickerContainer) return;
    this.pickerContainer.classList.remove('visible', 'mode-all', 'mode-ally');
    this.pickerContainer.innerHTML = '';
  }
}

/** Hover tooltip explaining the Break mechanic. Mirrors the broken status description. */
function buildBreakTooltip(): string {
  return (
    `<div class="tooltip-inner">` +
      `<div class="tooltip-header"><span class="tooltip-name">Break</span></div>` +
      `<div class="tooltip-separator"></div>` +
      `<div class="tooltip-line"><span class="value">Fills as the target takes damage.</span></div>` +
      `<div class="tooltip-line"><span class="value">When full, the target is Broken for 2 turns: +25% Damage taken, Momentum delayed, debuffs applied during the window last +1 turn.</span></div>` +
    `</div>`
  );
}

/** Build the hover tooltip showing each barrier instance's source, amount, and duration. */
function buildBarrierTooltip(barriers: ServerBarrierInstance[]): string {
  const total = sumBarriers(barriers);
  const sorted = [...barriers].sort((a, b) => b.amount - a.amount);
  const lines: string[] = [];
  lines.push(`<div class="tooltip-inner">`);
  lines.push(`<div class="tooltip-header"><span class="tooltip-name">Total Barrier: ${total}</span></div>`);
  lines.push(`<div class="tooltip-separator"></div>`);
  for (const b of sorted) {
    const color = barrierColor(b.sourceId);
    const dur = b.turnsRemaining >= 99 ? '∞' : `${b.turnsRemaining}t`;
    lines.push(
      `<div class="tooltip-line">` +
        `<span class="barrier-tooltip-swatch" style="background:${color}"></span>` +
        `<span class="value">${b.sourceName}</span>` +
        `<span class="label">${b.amount} · ${dur}</span>` +
      `</div>`
    );
  }
  lines.push(`</div>`);
  return lines.join('');
}
